import React, { useState, useEffect, useMemo } from 'react';
import styles from './index.module.scss';

const QUESTIONS = [
  {
    id: 'Q1',
    title: '您的样品形态是？（物理维度）',
    options: {
      A: '标准圆形晶圆（4/6/8英寸完整晶圆）',
      B: '非标准样品（碎片/不规则/异形样品）',
      C: '混合情况（既有整圆也有碎片）'
    }
  },
  {
    id: 'Q2',
    title: '您的生产目标是？（业务维度）',
    options: {
      A: '工艺探索（工艺配方开发、参数优化）',
      B: '小批量验证（样品制备、批次稳定性测试）',
      C: '规模量产（固定工艺配方、SPC质量管控）。'
    }
  },
  {
    id: 'Q3',
    title: '工艺成熟度预期如何？',
    options: {
      A: '成熟工艺 (Standard Process): 复用现有标准模块（如标准CMOS/MEMS流程）。',
      B: '前沿/探索性研究 (Experimental): 涉及新材料或未知参数，需频繁验证。'
    }
  },
  {
    id: 'Q4',
    title: '器件图形对边缘质量（Line Edge Roughness, LER）的要求？',
    options: {
      A: '常规要求： 边缘粗糙度容忍度高，适用于大尺寸结构。',
      B: '中等要求： 需要较平滑边缘以保证器件性能',
      C: '严格要求： LER 必须极低，适用于高速器件、纳米结构、光学器件。'
    }
  },
  {
    id: 'Q5',
    title: '器件结构涉及哪些加工面（拓扑结构）？',
    options: {
      A: '仅正面 (Front-side only): 绝大多数芯片，所有工艺都在一个面上完成。',
      B: '双面加工 (Double-side): 需要制作背腔（如压力传感器）、通孔（TSV）或背面电极。'
    }
  },
  {
    id: 'Q6',
    title: '是否需要独立的“零层”对准标记 (Zero-Layer Marker)？',
    options: {
      A: '不需要: 利用第一层功能图形本身作为对准基准（适用于微米级大尺寸器件）。',
      B: '需要: 第一层图形太小、对比度太低，或者使用EBL，必须先制作一套专用的高衬度金属标记。',
      C: '不确定'
    }
  },
  {
    id: 'Q7',
    title: '核心材料对温度的敏感程度（热预算）？',
    options: {
      A: '耐高温 (> 400°C): 如硅、二氧化硅、难熔金属（如钨W）。',
      B: '中温限制 (150°C - 400°C): 如铝/铜互连、聚酰亚胺(PI)、磁性薄膜。',
      C: '极度敏感 (< 150°C): 如生物分子、有机半导体、普通光刻胶。'
    }
  },
  {
    id: 'Q8',
    title: '敏感材料的化学耐受性（溶剂禁忌）？',
    options: {
      A: '标准耐受: 耐丙酮、醇类、酸碱。',
      B: '有机敏感: 怕丙酮、怕强极性溶剂（如NMP）。',
      C: '酸/碱敏感: 怕腐蚀液（如铝电极怕显影液）。'
    }
  },
  {
    id: 'Q9',
    title: '是否包含特殊或易污染材料？',
    options: {
      A: '标准材料: Si, SiO2, Al, Cu。',
      B: '磁性/贵金属: Co, Fe, Ni, Au, Pt (需要特殊刻蚀)。'
    }
  },
  {
    id: 'Q10',
    title: '是否包含悬浮或可动的机械结构 (MEMS特征)？',
    options: {
      A: '否: 实心薄膜器件。',
      B: '是: 悬臂梁、薄膜、微流道等。'
    }
  }
];

const formatAnswerText = (answers) => {
  return QUESTIONS.map((q) => `${q.id}：${answers[q.id]}`).join(' ');
};

const isComplete = (answers) => {
  return QUESTIONS.every((q) => Boolean(answers[q.id]));
};

const InitialQuestionnaire = ({
  visible,
  mode,
  initialAnswers,
  initialView,
  onClose,
  onComplete,
  deviceName,
  onDeviceNameChange
}) => {
  const readOnly = mode === 'view';
  const [view, setView] = useState('form'); // form | overview
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const completed = useMemo(() => isComplete(answers), [answers]);
  // stepIndex: 0=器件名称；1..N=Q1..QN
  const isLastStep = stepIndex === QUESTIONS.length;
  const currentQuestion = stepIndex === 0 ? null : QUESTIONS[stepIndex - 1];

  useEffect(() => {
    if (!visible) return;

    if (mode === 'view') {
      setAnswers(initialAnswers || {});
      setStepIndex(QUESTIONS.length);
      setView('overview');
      setIsSubmitting(false);
      return;
    }

    if (mode === 'edit') {
      setAnswers(initialAnswers || {});
      setStepIndex(0);
      setView(initialView === 'overview' ? 'overview' : 'form');
      setIsSubmitting(false);
      return;
    }

    // mode === create
    setAnswers({});
    setStepIndex(0);
    setView('form');
    setIsSubmitting(false);
  }, [visible, mode, initialAnswers, initialView]);

  const handleSelect = (letter) => {
    if (!currentQuestion) return;
    if (readOnly) return;

    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: letter
    }));

    if (view !== 'form') return;

    // 选择即前进：提升答题效率（最后一题不自动跳）
    if (stepIndex < QUESTIONS.length) {
      setStepIndex((prev) => Math.min(QUESTIONS.length, prev + 1));
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setStepIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();

    // 第一步：器件名称
    if (stepIndex === 0) {
      if (String(deviceName || '').trim()) {
        setStepIndex(1);
      }
      return;
    }

    if (currentQuestion && answers[currentQuestion.id]) {
      setStepIndex((prev) => Math.min(QUESTIONS.length, prev + 1));
    }
  };

  const handleFinishToOverview = (e) => {
    e.stopPropagation();
    if (!completed) return;
    if (readOnly) return;
    setView('overview');
  };

  const handleGoOverview = (e) => {
    e.stopPropagation();
    setView('overview');
  };

  const handleGoForm = (e) => {
    e.stopPropagation();
    if (readOnly) return;
    setView('form');
  };

  const handleClickOverviewItem = (index) => {
    if (readOnly) return;
    // 总览里 index 对应 QUESTIONS 数组下标（Q1..QN）
    setStepIndex(index + 1);
    setView('form');
  };

  const handleSubmit = async (e) => {
    e.stopPropagation();
    if (!completed || isSubmitting || readOnly) return;

    setIsSubmitting(true);
    try {
      const answerText = formatAnswerText(answers);
      await onComplete?.({ answers, answerText });
      onClose?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>工艺需求问卷</h3>
          <button
            className={styles.closeButton}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            aria-label="关闭"
          >
            ×
          </button>
        </div>

        {view === 'form' ? (
          <>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${((stepIndex + 1) / (QUESTIONS.length + 1)) * 100}%` }}
              />
              <div className={styles.progressText}>
                问题 {stepIndex + 1} / {QUESTIONS.length + 1}
              </div>
            </div>

            {stepIndex === 0 ? (
              <div className={styles.content}>
                <div className={styles.questionContainer}>
                  <h4 className={styles.questionTitle}>
                    请输入您想制备的器件
                  </h4>

                  <div className={styles.options}>
                    <input
                      type="text"
                      value={deviceName || ''}
                      onChange={(e) => {
                        if (readOnly) return;
                        onDeviceNameChange?.(e.target.value);
                      }}
                      placeholder="例如：MEMS压力传感器、石墨烯器件..."
                      className={styles.deviceNameInput}
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.content}>
                <div className={styles.questionContainer}>
                  <h4 className={styles.questionTitle}>
                    {currentQuestion.id}. {currentQuestion.title}
                  </h4>

                  <div className={styles.options}>
                    {Object.entries(currentQuestion.options).map(([letter, text]) => (
                      <div
                        key={letter}
                        className={`${styles.option} ${answers[currentQuestion.id] === letter ? styles.selected : ''} ${readOnly ? styles.readOnlyOption : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (readOnly) return;
                          handleSelect(letter);
                        }}
                      >
                        <div className={styles.optionLetter}>{letter}</div>
                        <div className={styles.optionText}>{text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.footer}>
              <button
                className={`${styles.navButton} ${styles.prevButton}`}
                onClick={handlePrev}
                disabled={stepIndex === 0}
              >
                上一题
              </button>

              {!isLastStep ? (
                <button
                  className={`${styles.navButton} ${styles.nextButton}`}
                  onClick={handleNext}
                  disabled={readOnly || (stepIndex === 0 ? !String(deviceName || '').trim() : !answers[currentQuestion.id])}
                >
                  下一题
                </button>
              ) : (
                <button
                  className={`${styles.submitButton} ${!completed || isSubmitting ? styles.disabled : ''}`}
                  onClick={handleFinishToOverview}
                  disabled={readOnly || !completed || isSubmitting}
                >
                  预览总览
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.overviewHeader}>
              <div className={styles.overviewTitle}>总览（点击任意问题可修改）</div>
              <div className={styles.overviewActions}>
                <button className={styles.overviewBackButton} onClick={handleGoForm}>
                  继续修改
                </button>
                <button
                  className={`${styles.submitButton} ${!completed || isSubmitting ? styles.disabled : ''}`}
                  onClick={handleSubmit}
                  disabled={!completed || isSubmitting}
                >
                  {isSubmitting ? '提交中...' : (mode === 'edit' ? '完成修改' : '完成问卷')}
                </button>
              </div>
            </div>

            <div className={styles.overviewContent}>
              {QUESTIONS.map((q, idx) => {
                const selected = answers[q.id];
                const selectedText = selected ? q.options[selected] : '';
                return (
                  <div
                    key={q.id}
                    className={styles.overviewItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickOverviewItem(idx);
                    }}
                  >
                    <div className={styles.overviewQuestionRow}>
                      <div className={styles.overviewQId}>{q.id}</div>
                      <div className={styles.overviewQTitle}>{q.title}</div>
                      <div className={styles.overviewAnswerTag}>{selected || '-'}</div>
                    </div>
                    <div className={styles.overviewAnswerText}>{selected ? selectedText : '未选择'}</div>
                  </div>
                );
              })}
            </div>

            <div className={styles.footer}>
              <button className={`${styles.navButton} ${styles.prevButton}`} onClick={handleGoOverview}>
                返回总览
              </button>
              {!readOnly && (
                <button
                  className={`${styles.submitButton} ${!completed || isSubmitting ? styles.disabled : ''}`}
                  onClick={handleSubmit}
                  disabled={!completed || isSubmitting}
                >
                  {isSubmitting ? '提交中...' : (mode === 'edit' ? '完成修改' : '完成问卷')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InitialQuestionnaire;
export { QUESTIONS, formatAnswerText };
